import { useState, useEffect, useMemo } from 'react'
import { alertasAPI } from '../services/api'
import { useAlertStore, useAuthStore, ROLES, PERMISSIONS } from '../store'
import {
  Bell, AlertTriangle, Info, CheckCircle, Clock, Building2,
  X, Trash2, CheckCheck, Filter, Search, TrendingUp, TrendingDown,
  Volume2, VolumeX, Eye, MoreVertical, RefreshCw, Plus, Wrench,
  Users, Package, Stethoscope, AlertOctagon
} from 'lucide-react'
import toast from 'react-hot-toast'

// Tipos de alertas operativas
const TIPOS_ALERTA = [
  { id: 'urgente', nombre: 'Urgente', icon: AlertOctagon, color: 'red', description: 'Situaciones criticas que requieren atencion inmediata' },
  { id: 'advertencia', nombre: 'Advertencia', icon: AlertTriangle, color: 'amber', description: 'Problemas que requieren atencion pronto' },
  { id: 'info', nombre: 'Informativa', icon: Info, color: 'blue', description: 'Avisos generales y actualizaciones' },
]

// Categorias de alertas operativas
const CATEGORIAS_ALERTA = [
  { id: 'quirofano', nombre: 'Quirofano', icon: Wrench, placeholder: 'Ej: Quirofano Q-3 requiere mantenimiento' },
  { id: 'personal', nombre: 'Personal', icon: Users, placeholder: 'Ej: Falta anestesiologo turno vespertino' },
  { id: 'insumos', nombre: 'Insumos', icon: Package, placeholder: 'Ej: Stock bajo de suturas' },
  { id: 'cirugia', nombre: 'Cirugia', icon: Stethoscope, placeholder: 'Ej: Cirugia de emergencia programada' },
  { id: 'otro', nombre: 'Otro', icon: Bell, placeholder: 'Describe la situacion' },
]

// Datos demo - con hospital_id para filtrado
const DEMO_ALERTAS = [
  { id: 1, tipo: 'urgente', categoria: 'quirofano', titulo: 'Quirofano Q-3 fuera de servicio', mensaje: 'El quirofano Q-3 requiere mantenimiento urgente. Se han reprogramado 3 cirugias.', hospital_id: 2, hospital_nombre: 'HGZ No. 57', created_at: '2024-12-29T10:30:00', leida: false, creado_por: 'Operador HGZ-57' },
  { id: 2, tipo: 'advertencia', categoria: 'quirofano', titulo: 'Alta ocupacion de quirofanos', mensaje: 'La ocupacion de quirofanos ha superado el 90%. Se recomienda evaluar reprogramaciones.', hospital_id: 4, hospital_nombre: 'HGZ No. 76', created_at: '2024-12-29T09:15:00', leida: false, creado_por: 'Operador HGZ-76' },
  { id: 3, tipo: 'info', categoria: 'quirofano', titulo: 'Nuevo equipo disponible', mensaje: 'Se ha instalado nuevo equipo de laparoscopia en el quirofano Q-1.', hospital_id: 1, hospital_nombre: 'HGZ No. 53', created_at: '2024-12-28T16:45:00', leida: true, creado_por: 'Operador HGZ-53' },
  { id: 4, tipo: 'urgente', categoria: 'cirugia', titulo: 'Cirugia de emergencia programada', mensaje: 'Se ha programado una apendicectomia de emergencia para las 14:00 hrs. Preparar quirofano Q-2.', hospital_id: 3, hospital_nombre: 'HGZ No. 68', created_at: '2024-12-29T11:00:00', leida: false, creado_por: 'Operador HGZ-68' },
  { id: 5, tipo: 'advertencia', categoria: 'cirugia', titulo: 'Retraso en cirugias programadas', mensaje: '4 cirugias programadas para hoy tienen un retraso promedio de 45 minutos.', hospital_id: 5, hospital_nombre: 'HGR No. 196', created_at: '2024-12-29T08:30:00', leida: true, creado_por: 'Operador HGR-196' },
  { id: 6, tipo: 'info', categoria: 'otro', titulo: 'Capacitacion programada', mensaje: 'Recordatorio: Manana hay capacitacion sobre nuevos protocolos quirurgicos a las 8:00 hrs.', hospital_id: null, hospital_nombre: 'Todos', created_at: '2024-12-28T14:00:00', leida: true, creado_por: 'Administrador' },
  { id: 7, tipo: 'urgente', categoria: 'insumos', titulo: 'Falta de insumos criticos', mensaje: 'El stock de suturas absorbibles esta por debajo del minimo. Solicitar reabastecimiento urgente.', hospital_id: 1, hospital_nombre: 'HGZ No. 53', created_at: '2024-12-29T07:00:00', leida: false, creado_por: 'Operador HGZ-53' },
  { id: 8, tipo: 'advertencia', categoria: 'personal', titulo: 'Personal insuficiente', mensaje: 'El turno vespertino cuenta con 2 anestesiologos menos de lo requerido para las cirugias programadas.', hospital_id: 3, hospital_nombre: 'HGZ No. 68', created_at: '2024-12-29T06:30:00', leida: false, creado_por: 'Operador HGZ-68' },
]

// Contador animado
const AnimatedCounter = ({ value, duration = 1000, showDashOnZero = false }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const end = parseInt(value) || 0
    if (end === 0) {
      setCount(0)
      return
    }

    const startTime = Date.now()
    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(end * easeOut))
      if (progress < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
  }, [value, duration])

  if (showDashOnZero && (!value || value === 0)) return <span>--</span>
  return <span>{count}</span>
}

// KPI Card
const KPICard = ({ title, value, subtitle, icon: IconComponent, color, onClick, isActive, showDashOnZero = false }) => {
  const colorClasses = {
    emerald: 'from-emerald-500 to-emerald-600',
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600',
    gray: 'from-gray-500 to-gray-600',
  }

  const showDash = showDashOnZero && (!value || value === 0)

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClasses[color]} p-5 text-white shadow-lg transition-all duration-300 ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-xl' : ''} ${isActive ? 'ring-4 ring-white/50 scale-[1.02]' : ''}`}
    >
      {isActive && (
        <div className="absolute top-2 right-2">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <div className="text-4xl font-bold mb-1 tracking-tight">
            {showDash ? '--' : <AnimatedCounter value={value} />}
          </div>
          <p className="text-white/70 text-xs">{subtitle}</p>
        </div>
        <div className="p-3 bg-white/20 rounded-xl">
          <IconComponent className="w-6 h-6" />
        </div>
      </div>
    </div>
  )
}

// Modal de detalle de alerta
const AlertDetailModal = ({ alerta, onClose, onMarkAsRead, onDelete }) => {
  if (!alerta) return null

  const getTypeConfig = (tipo) => {
    switch (tipo) {
      case 'urgente':
        return { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, iconColor: 'text-red-500', label: 'Urgente' }
      case 'advertencia':
        return { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle, iconColor: 'text-amber-500', label: 'Advertencia' }
      case 'info':
        return { bg: 'bg-blue-100', text: 'text-blue-700', icon: Info, iconColor: 'text-blue-500', label: 'Informativa' }
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-700', icon: Bell, iconColor: 'text-gray-500', label: 'General' }
    }
  }

  const config = getTypeConfig(alerta.tipo)
  const IconComponent = config.icon

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl ${config.bg}`}>
                <IconComponent className={`w-6 h-6 ${config.iconColor}`} />
              </div>
              <div>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} mb-2`}>
                  {config.label}
                </span>
                <h2 className="text-xl font-bold text-gray-800">{alerta.titulo}</h2>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-600 leading-relaxed">{alerta.mensaje}</p>

          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                {new Date(alerta.created_at).toLocaleString('es-MX', {
                  dateStyle: 'full',
                  timeStyle: 'short'
                })}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{alerta.hospital_nombre}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Eye className="w-4 h-4 text-gray-400" />
              <span className={alerta.leida ? 'text-emerald-600' : 'text-amber-600'}>
                {alerta.leida ? 'Leida' : 'Sin leer'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex gap-3">
          {!alerta.leida && (
            <button
              onClick={() => { onMarkAsRead(alerta.id); onClose(); }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              Marcar como leida
            </button>
          )}
          <button
            onClick={() => { onDelete(alerta.id); onClose(); }}
            className="flex items-center justify-center gap-2 px-4 py-3 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-5 h-5" />
            Eliminar
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal para crear nueva alerta operativa
const CreateAlertModal = ({ onClose, onCreate, hospitalNombre, hospitalId }) => {
  const [formData, setFormData] = useState({
    tipo: 'advertencia',
    categoria: 'quirofano',
    titulo: '',
    mensaje: '',
  })
  const [loading, setLoading] = useState(false)

  const selectedCategoria = CATEGORIAS_ALERTA.find(c => c.id === formData.categoria)

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.titulo.trim() || !formData.mensaje.trim()) {
      toast.error('Complete todos los campos')
      return
    }

    setLoading(true)
    try {
      const newAlert = {
        id: Date.now(),
        ...formData,
        hospital_id: hospitalId,
        hospital_nombre: hospitalNombre || 'Todos',
        created_at: new Date().toISOString(),
        leida: false,
        creado_por: `Operador ${hospitalNombre || 'Sistema'}`,
      }

      onCreate(newAlert)
      toast.success('Alerta creada exitosamente')
      onClose()
    } catch (error) {
      toast.error('Error al crear la alerta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Nueva Alerta Operativa</h2>
              <p className="text-sm text-gray-500 mt-1">
                {hospitalNombre ? `Para: ${hospitalNombre}` : 'Alerta general'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Tipo de alerta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prioridad
            </label>
            <div className="grid grid-cols-3 gap-2">
              {TIPOS_ALERTA.map((tipo) => {
                const Icon = tipo.icon
                const isSelected = formData.tipo === tipo.id
                const colorMap = {
                  red: isSelected ? 'border-red-500 bg-red-50 text-red-700' : '',
                  amber: isSelected ? 'border-amber-500 bg-amber-50 text-amber-700' : '',
                  blue: isSelected ? 'border-blue-500 bg-blue-50 text-blue-700' : '',
                }
                return (
                  <button
                    key={tipo.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo: tipo.id })}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                      isSelected
                        ? colorMap[tipo.color]
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{tipo.nombre}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Categoria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoria
            </label>
            <div className="grid grid-cols-5 gap-2">
              {CATEGORIAS_ALERTA.map((cat) => {
                const Icon = cat.icon
                const isSelected = formData.categoria === cat.id
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, categoria: cat.id })}
                    className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-500'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] font-medium">{cat.nombre}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Titulo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titulo
            </label>
            <input
              type="text"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              placeholder={selectedCategoria?.placeholder || 'Titulo de la alerta'}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              required
            />
          </div>

          {/* Mensaje */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripcion detallada
            </label>
            <textarea
              value={formData.mensaje}
              onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
              placeholder="Proporcione detalles sobre la situacion, acciones requeridas, etc."
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              required
            />
          </div>

          {/* Preview */}
          <div className={`p-4 rounded-xl border-l-4 ${
            formData.tipo === 'urgente' ? 'border-l-red-500 bg-red-50' :
            formData.tipo === 'advertencia' ? 'border-l-amber-500 bg-amber-50' :
            'border-l-blue-500 bg-blue-50'
          }`}>
            <p className="text-xs text-gray-500 mb-1">Vista previa:</p>
            <p className="font-medium text-gray-800">{formData.titulo || 'Titulo de la alerta'}</p>
            <p className="text-sm text-gray-600 mt-1">{formData.mensaje || 'Descripcion de la alerta...'}</p>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                formData.tipo === 'urgente' ? 'bg-red-600 hover:bg-red-700 text-white' :
                formData.tipo === 'advertencia' ? 'bg-amber-600 hover:bg-amber-700 text-white' :
                'bg-blue-600 hover:bg-blue-700 text-white'
              } disabled:opacity-50`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Crear Alerta
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Alertas() {
  const [alertas, setAlertas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterTipo, setFilterTipo] = useState('')
  const [filterLeida, setFilterLeida] = useState('') // '', 'leida', 'no_leida'
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAlerta, setSelectedAlerta] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const setUnreadCount = useAlertStore(state => state.setUnreadCount)

  // Info del usuario
  const user = useAuthStore(state => state.user)
  const hasPermission = useAuthStore(state => state.hasPermission)

  // Verificar permisos
  const canCreateAlerts = hasPermission('canCreateAlerts')
  const isOperador = user?.rol === ROLES.OPERADOR
  const isAdmin = user?.rol === ROLES.ADMIN

  useEffect(() => {
    loadAlertas()
  }, [user?.hospital_id, isOperador])

  // Actualizar contador global de no leidas
  useEffect(() => {
    const unread = alertas.filter(a => !a.leida).length
    setUnreadCount(unread)
  }, [alertas, setUnreadCount])

  const loadAlertas = async () => {
    try {
      const response = await alertasAPI.getAll()
      let data = response.data?.data || response.data || []

      // Si es operador, filtrar solo las alertas de su hospital
      if (isOperador && user?.hospital_id) {
        data = data.filter(a => a.hospital_id === user.hospital_id || a.hospital_id === null)
      }

      setAlertas(data)
    } catch (error) {
      console.error('Error loading alertas, usando datos demo:', error)
      // Filtrar datos demo también
      let demoData = [...DEMO_ALERTAS]
      if (isOperador && user?.hospital_id) {
        demoData = demoData.filter(a => a.hospital_id === user.hospital_id || a.hospital_id === null)
      }
      setAlertas(demoData)
    } finally {
      setLoading(false)
    }
  }

  // Crear nueva alerta
  const handleCreateAlert = (newAlert) => {
    setAlertas(prev => [newAlert, ...prev])
  }

  const handleMarkAsRead = (alertaId) => {
    setAlertas(alertas.map(a =>
      a.id === alertaId ? { ...a, leida: true } : a
    ))
    toast.success('Alerta marcada como leida')
  }

  const handleMarkAllAsRead = () => {
    setAlertas(alertas.map(a => ({ ...a, leida: true })))
    toast.success('Todas las alertas marcadas como leidas')
  }

  const handleDelete = (alertaId) => {
    setAlertas(alertas.filter(a => a.id !== alertaId))
    toast.success('Alerta eliminada')
  }

  const handleDeleteAll = () => {
    const readAlertas = alertas.filter(a => a.leida)
    if (readAlertas.length === 0) {
      toast.error('No hay alertas leidas para eliminar')
      return
    }
    setAlertas(alertas.filter(a => !a.leida))
    toast.success(`${readAlertas.length} alertas eliminadas`)
  }

  // Stats
  const stats = useMemo(() => {
    return {
      total: alertas.length,
      noLeidas: alertas.filter(a => !a.leida).length,
      urgentes: alertas.filter(a => a.tipo === 'urgente').length,
      advertencias: alertas.filter(a => a.tipo === 'advertencia').length,
      info: alertas.filter(a => a.tipo === 'info').length,
    }
  }, [alertas])

  // Filtrado
  const filteredAlertas = useMemo(() => {
    return alertas.filter(a => {
      const matchTipo = !filterTipo || a.tipo === filterTipo
      const matchLeida = !filterLeida ||
        (filterLeida === 'leida' && a.leida) ||
        (filterLeida === 'no_leida' && !a.leida)
      const matchSearch = !searchTerm ||
        a.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.mensaje.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.hospital_nombre?.toLowerCase().includes(searchTerm.toLowerCase())

      return matchTipo && matchLeida && matchSearch
    })
  }, [alertas, filterTipo, filterLeida, searchTerm])

  // Reset filtros
  const resetFilters = () => {
    setFilterTipo('')
    setFilterLeida('')
    setSearchTerm('')
  }

  const hasActiveFilters = filterTipo || filterLeida || searchTerm

  const getAlertStyle = (tipo, leida) => {
    const baseStyle = leida ? 'opacity-60' : ''
    switch (tipo) {
      case 'urgente':
        return `border-l-red-500 bg-red-50 hover:bg-red-100 ${baseStyle}`
      case 'advertencia':
        return `border-l-amber-500 bg-amber-50 hover:bg-amber-100 ${baseStyle}`
      case 'info':
        return `border-l-blue-500 bg-blue-50 hover:bg-blue-100 ${baseStyle}`
      default:
        return `border-l-gray-500 bg-gray-50 hover:bg-gray-100 ${baseStyle}`
    }
  }

  const getAlertIcon = (tipo) => {
    switch (tipo) {
      case 'urgente':
        return <AlertTriangle className="text-red-500" size={24} />
      case 'advertencia':
        return <AlertTriangle className="text-amber-500" size={24} />
      case 'info':
        return <Info className="text-blue-500" size={24} />
      default:
        return <Bell className="text-gray-500" size={24} />
    }
  }

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) return `hace ${diffMins} min`
    if (diffHours < 24) return `hace ${diffHours}h`
    if (diffDays < 7) return `hace ${diffDays}d`
    return date.toLocaleDateString('es-MX')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">Centro de Alertas</h1>
            {isOperador && user?.hospital_nombre && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {user.hospital_nombre}
              </span>
            )}
          </div>
          <p className="text-gray-500">
            {filteredAlertas.length} de {alertas.length} alertas
            {isOperador && ' de tu hospital'}
            {hasActiveFilters && (
              <button onClick={resetFilters} className="ml-2 text-emerald-600 hover:text-emerald-700 text-sm">
                (Limpiar filtros)
              </button>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Boton crear alerta - solo para operadores y admin */}
          {canCreateAlerts && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Nueva Alerta
            </button>
          )}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-2 rounded-lg border transition-colors ${
              soundEnabled ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : 'bg-gray-100 border-gray-200 text-gray-500'
            }`}
            title={soundEnabled ? 'Silenciar alertas' : 'Activar sonido'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <button
            onClick={loadAlertas}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            title="Actualizar"
          >
            <RefreshCw className="w-5 h-5 text-gray-500" />
          </button>
          <button
            onClick={handleMarkAllAsRead}
            disabled={stats.noLeidas === 0}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <CheckCheck className="w-4 h-4" />
            Marcar todas
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Sin Leer"
          value={stats.noLeidas}
          subtitle={filterLeida === 'no_leida' ? "Filtro activo" : "Requieren atencion"}
          icon={Bell}
          color="purple"
          onClick={() => setFilterLeida(filterLeida === 'no_leida' ? '' : 'no_leida')}
          isActive={filterLeida === 'no_leida'}
          showDashOnZero={true}
        />
        <KPICard
          title="Urgentes"
          value={stats.urgentes}
          subtitle={filterTipo === 'urgente' ? "Filtro activo" : "Alta prioridad"}
          icon={AlertTriangle}
          color="red"
          onClick={() => setFilterTipo(filterTipo === 'urgente' ? '' : 'urgente')}
          isActive={filterTipo === 'urgente'}
          showDashOnZero={true}
        />
        <KPICard
          title="Advertencias"
          value={stats.advertencias}
          subtitle={filterTipo === 'advertencia' ? "Filtro activo" : "Prioridad media"}
          icon={AlertTriangle}
          color="amber"
          onClick={() => setFilterTipo(filterTipo === 'advertencia' ? '' : 'advertencia')}
          isActive={filterTipo === 'advertencia'}
          showDashOnZero={true}
        />
        <KPICard
          title="Informativas"
          value={stats.info}
          subtitle={filterTipo === 'info' ? "Filtro activo" : "Solo informacion"}
          icon={Info}
          color="blue"
          onClick={() => setFilterTipo(filterTipo === 'info' ? '' : 'info')}
          isActive={filterTipo === 'info'}
          showDashOnZero={true}
        />
      </div>

      {/* Filtros y busqueda */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar en alertas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={filterLeida}
              onChange={(e) => setFilterLeida(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Todas</option>
              <option value="no_leida">Sin leer</option>
              <option value="leida">Leidas</option>
            </select>
            <button
              onClick={handleDeleteAll}
              className="flex items-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Limpiar leidas
            </button>
          </div>
        </div>
      </div>

      {/* Lista de alertas */}
      <div className="space-y-3">
        {filteredAlertas.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg mb-2">No hay alertas para mostrar</p>
            <p className="text-gray-400 text-sm mb-4">
              {hasActiveFilters ? 'Intenta ajustar los filtros de busqueda' : 'Todo esta en orden'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          filteredAlertas.map((alerta) => (
            <div
              key={alerta.id}
              onClick={() => setSelectedAlerta(alerta)}
              className={`border-l-4 rounded-xl shadow-sm p-4 transition-all cursor-pointer ${getAlertStyle(alerta.tipo, alerta.leida)}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  {getAlertIcon(alerta.tipo)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-800 truncate">{alerta.titulo}</h3>
                        {!alerta.leida && (
                          <span className="flex-shrink-0 w-2 h-2 bg-emerald-500 rounded-full" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{alerta.mensaje}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!alerta.leida && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(alerta.id); }}
                          className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                          title="Marcar como leida"
                        >
                          <CheckCircle size={20} className="text-emerald-500" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(alerta.id); }}
                        className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={18} className="text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{getTimeAgo(alerta.created_at)}</span>
                    </div>
                    {alerta.hospital_nombre && (
                      <div className="flex items-center gap-1">
                        <Building2 size={12} />
                        <span>{alerta.hospital_nombre}</span>
                      </div>
                    )}
                    {alerta.leida && (
                      <span className="text-emerald-600 flex items-center gap-1">
                        <CheckCircle size={12} />
                        Leida
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de detalle */}
      {selectedAlerta && (
        <AlertDetailModal
          alerta={selectedAlerta}
          onClose={() => setSelectedAlerta(null)}
          onMarkAsRead={handleMarkAsRead}
          onDelete={handleDelete}
        />
      )}

      {/* Modal de crear alerta */}
      {showCreateModal && (
        <CreateAlertModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateAlert}
          hospitalNombre={user?.hospital_nombre}
          hospitalId={user?.hospital_id}
        />
      )}
    </div>
  )
}
